import SearchBarInput from '@navi/web-ui/lib/components/SearchBarInput';
import { FilterIconFilled, SearchIcon } from '@navi/web-ui/lib/icons';
import { BorderedInput, Button, Drawer } from '@navi/web-ui/lib/primitives';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'src/store';
import { createQueryParams } from 'src/utils/QueryParamsHelper';
import { _map, isFunction, isObjectEmpty } from 'src/utils/commonUtils';
import { getQueryString } from '.';
import SelectedFiltersOnDashboard from './SelectedFiltersOnDashboard';
import styles from './TeamLead.module.scss';
import TeamLeadFilterCheckbox from './TeamLeadFilterCheckbox';
import { TeamLeadRenderFilters } from './TeamLeadRenderFilters';
import { FiltersKeys, ISelectedFilters, TABLE_NAME } from './interface';
import { GenericObject } from '@cp/src/types/CommonConstans';

type TeamLeadFiltersProps = {
  onFilteredApplied?: () => void;
  onFilterCleared?: () => void;
  isSearchNeeded?: boolean;
};

const TeamLeadFilters = ({
  onFilteredApplied,
  onFilterCleared,
  isSearchNeeded = true
}: TeamLeadFiltersProps) => {
  const filters = useSelector((state: RootState) => state.teamLeadDashboard.filters);
  const loading = useSelector((state: RootState) => state.teamLeadDashboard.loading);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(
    filters[FiltersKeys[0].key as keyof typeof filters]
  );
  const [activeFilters, setActiveFilters] = useState<string>(FiltersKeys[0].key);
  const [selectedFilters, setSelectedFilters] = useState<ISelectedFilters>({});
  const [filteredFilters, setFilteredFilters] = useState<Array<string>>();
  const [searchedKeyWord, setSearchedKeyWord] = useState<string>();
  const navigate = useNavigate();
  useEffect(() => {
    setSelectedOptions(filters[FiltersKeys[0].key as keyof typeof filters]);
  }, [filters]);

  const isSelectedFiltersEmpty = (selectedFilters: GenericObject) => {
    if (isObjectEmpty(selectedFilters)) {
      return true;
    }
    for (const key in selectedFilters) {
      if (selectedFilters[key]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (isFunction(onFilteredApplied)) {
      onFilteredApplied();
    }
    if (isFunction(onFilterCleared) && isSelectedFiltersEmpty(selectedFilters)) {
      onFilterCleared();
    }
  }, [selectedFilters]);

  useEffect(() => {
    const queryString = getQueryString();
    if (!loading && queryString?.lastUsed === 'query') {
      searchInputRef?.current?.focus();
    }
  }, [loading]);

  // this is to set initial filters
  useEffect(() => {
    const selectedFilter = {};
    const queryString = getQueryString();
    for (const tab in FiltersKeys) {
      const key = FiltersKeys[tab].key;
      const selectedFilters = queryString?.[key as keyof typeof queryString];
      const data = selectedFilters?.split(',')?.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});
      selectedFilter[key] = data;
    }
    setSelectedFilters(selectedFilter);
  }, [showFilterDrawer]);

  const handlerFilterCTAClick = () => {
    setShowFilterDrawer(true);
  };
  const handleFilterSelection = (event: SyntheticEvent) => {
    event.preventDefault();
    setActiveFilters(event.currentTarget.id);
    const options = filters[event.currentTarget.id as keyof typeof filters];
    setSelectedOptions(options as any);
  };

  const handleSelectedFilter = (selectedFilter: string, option: string) => {
    const selectedFiltersCopy: any = { ...selectedFilters };
    if (selectedFiltersCopy[selectedFilter]) {
      if (selectedFiltersCopy[selectedFilter][option]) {
        delete selectedFiltersCopy[selectedFilter][option];
      } else {
        selectedFiltersCopy[selectedFilter][option] = true;
      }
    } else {
      selectedFiltersCopy[selectedFilter] = { [option]: true };
    }
    setSelectedFilters(selectedFiltersCopy);
  };

  const renderOptions = (selectedFilter: string) => {
    const data = [];
    const isArray = Array.isArray(selectedOptions);
    if (searchedKeyWord && filteredFilters) {
      _map(filteredFilters, option => {
        data.push(
          <TeamLeadFilterCheckbox
            key={option}
            option={option}
            selectedFilter={selectedFilter}
            isArray={isArray}
            type="searched"
            handleSelectedFilter={handleSelectedFilter}
            selectedOptions={selectedOptions}
            selectedFilters={selectedFilters}
          />
        );
      });
      return data;
    }
    _map(selectedOptions, option => {
      data.push(
        <TeamLeadFilterCheckbox
          key={option}
          option={option}
          selectedFilter={selectedFilter}
          isArray={isArray}
          type="unSelected"
          handleSelectedFilter={handleSelectedFilter}
          selectedOptions={selectedOptions}
          selectedFilters={selectedFilters}
        />
      );
    });
    return data;
  };

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchedKeyWord(value);
    if (activeFilters === 'agentReferenceId') {
      const currentFilter = filters[activeFilters as keyof typeof filters];
      const filteredObj = Object.entries(currentFilter).filter(([refId, agentName]) => {
        return agentName.toLowerCase().includes(value.toLowerCase());
      });
      setFilteredFilters(filteredObj.map(([refId, agentName]) => refId));
    } else {
      const modifiedValue = value.replaceAll(' ', '_').toUpperCase();
      const filteredOptions = filters[activeFilters as keyof typeof filters].filter(option =>
        option.includes(modifiedValue)
      );
      setFilteredFilters(filteredOptions);
    }
  };

  const handleApplyFilters = () => {
    const queryString = getQueryString();
    const queryParamsPayload: Record<string, Array<string>> = { ...queryString };
    let key: keyof typeof selectedFilters;
    for (key in selectedFilters) {
      queryParamsPayload[key] = Object.keys(
        selectedFilters?.[key as keyof typeof selectedFilters] ?? []
      );
    }
    updateAndNavigate(queryParamsPayload);
  };
  const handleRemoveFilters = () => {
    const queryString = getQueryString();
    const queryParamsPayload: Record<string, Array<string>> = { ...queryString };
    let key: keyof typeof selectedFilters;
    for (key in selectedFilters) {
      delete queryParamsPayload?.[key];
    }
    setSelectedFilters({});
    updateAndNavigate(queryParamsPayload);
  };

  const updateAndNavigate = (params: Record<string, Array<string>>) => {
    const queryParams = createQueryParams({
      [TABLE_NAME]: { ...params, pageIndex: '0' }
    });
    navigate(queryParams);
    setShowFilterDrawer(false);
  };

  const removeSelectedFilter = (filter: string) => {
    const queryString = getQueryString();
    const queryParamsPayload: Record<string, Array<string>> = { ...queryString };
    delete queryParamsPayload[filter];
    setSelectedFilters({ ...selectedFilters, [filter]: undefined });
    updateAndNavigate(queryParamsPayload);
  };

  const handleQueryChange = (query: string) => {
    const queryString = getQueryString();
    const payload = { ...queryString };
    if (query.length > 0) {
      payload['query'] = query;
      payload['queryType'] = 'LAN';
      payload['lastUsed'] = 'query';
    } else {
      delete payload['query'];
      delete payload['queryType'];
    }
    updateAndNavigate(payload);
  };

  const handleSearchChange = (search: string) => {
    if (search.length === 0) {
      handleQueryChange('');
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleQueryChange(searchInputRef.current?.value || '');
  };

  return (
    <>
      <div className={styles.filterContainer}>
        <SelectedFiltersOnDashboard
          removeSelectedFilter={removeSelectedFilter}
          handleRemoveFilters={handleRemoveFilters}
          filters={filters}
        />
        <Button
          startAdornment={<FilterIconFilled color="var(--blue-base)" />}
          variant="secondary"
          onClick={handlerFilterCTAClick}
        >
          Filters
        </Button>
        {isSearchNeeded ? (
          <form onSubmit={handleFormSubmit}>
            <SearchBarInput
              title="Search by lan, press enter to search"
              ref={searchInputRef}
              containerClassName={styles.searchInputContainer}
              className={styles.searchInput}
              disabled={loading}
              debounceDelay={300}
              onSearchChange={handleSearchChange}
              value={getQueryString()?.query}
              LeftInputAdornment={<SearchIcon />}
              placeholder="Search here..."
            />
          </form>
        ) : null}
      </div>
      <Drawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        headerText="FILTER BY"
        showHeaderDivider={true}
        usePadding={false}
        style={{ width: 'auto', overflow: 'hidden' }}
      >
        <div className={styles.container}>
          <section className={styles.tabSection}>
            <section className={styles.filterContainer}>
              <TeamLeadRenderFilters
                handleFilterSelection={handleFilterSelection}
                selectedFilters={selectedFilters}
                activeFilters={activeFilters}
              />
            </section>
            <section className={styles.optionsContainer}>
              <div className={styles.searchInput}>
                <BorderedInput
                  onChange={handleSearchInput}
                  value={searchedKeyWord}
                  placeholder="Search"
                  LeftInputAdornment={<SearchIcon />}
                  containerClassName={styles.inputContainer}
                  style={{ width: '80%' }}
                />
              </div>
              <div key={Date.now()}>{renderOptions(activeFilters)}</div>
            </section>
          </section>

          <section className={styles.cta}>
            <Button variant="secondary" className={styles.button} onClick={handleRemoveFilters}>
              Clear All
            </Button>
            <Button onClick={handleApplyFilters} className={styles.button}>
              Apply
            </Button>
          </section>
        </div>
      </Drawer>
    </>
  );
};

export default TeamLeadFilters;
